import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Scrollbar } from 'app/components';
import { TransportGroupRoundedIcon } from 'app/icons';
import { autocolonNotSelectedItemsTransportSelector, divisionItemsTransportSelector } from 'app/selectors/transport';
import { useAsyncAction } from 'app/services';
import { createDivisionAutocolon } from 'app/store/transport';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const AutocolonDialog = ({ open, onClose }) => {
  const [type, setType] = useState();
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedAutocolons, setSelectedAutocolons] = useState([]);
  const [unselectedAutocolons, setUnselectedAutocolons] = useState([]);
  const [name, setName] = useState(null);

  const {
    asyncAction: create,
    isLoading: isCreateLoading,
    isSuccessful: isCreateSuccessful,
    isError: isCreateError,
  } = useAsyncAction(createDivisionAutocolon, [open]);

  const handleFormSubmit = () => {
    if (!type || !name) return;
    create({
      type,
      name,
      selectedAutocolons,
      id_division: selectedDivision,
    });
    handleClose();
  };

  const handleChangeType = (e) => {
    setName('');
    setSelectedDivision(null);
    setSelectedAutocolons([]);
    setType(e.target.value);
  };
  const handleChangeName = (e) => {
    setName(e.target.value);
  };

  const handleChangeDivision = (e) => {
    setSelectedDivision(e.target.value);
  };
  const handleClose = () => {
    onClose();
    setSelectedDivision(null);
    setType(null);
    setSelectedAutocolons([]);
    setName(null);
  };
  useEffect(() => {
    if (isCreateSuccessful) {
      handleClose();
    }
  }, [isCreateSuccessful]);

  const autocolons = useSelector(autocolonNotSelectedItemsTransportSelector);
  const divisions = useSelector(divisionItemsTransportSelector);

  const newAutocolons = () => {
    const selectedAutocolonsIds = selectedAutocolons.map((item) => item.id);
    return autocolons.filter((autocolon) => !selectedAutocolonsIds.includes(autocolon.id));
  };
  const handleToggleAutocolon = ({ id, name }) => {
    if (selectedAutocolons.find((item) => item.id === id)) {
      setSelectedAutocolons(selectedAutocolons.filter((item) => item.id !== id));
      if (!autocolons.find((autocolon) => autocolon.id === id)) {
        setUnselectedAutocolons([...unselectedAutocolons, { id, name }]);
      }
      return;
    }
    setSelectedAutocolons([...selectedAutocolons, { id, name }]);
    if (unselectedAutocolons.find((autocolon) => autocolon.id === id)) {
      setUnselectedAutocolons(unselectedAutocolons.filter((item) => item.id !== id));
    }
  };
  return (
    <Dialog
      maxWidth='md'
      fullWidth
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.MuiDialog-paper': {
          minWidth: '460px',
          minHeight: '460px',
        },
        '.MuiDialogTitle-root': {
          display: 'flex',
          padding: theme.spacing(2),
          alignItems: 'center',
        },
        '.MuiDialogTitle-root .MuiTypography-root': {
          marginLeft: theme.spacing(1.5),
        },
        '.MuiDialogTitle-root .MuiButtonBase-root': {
          marginLeft: 'auto',
        },
        '.MuiDialogContent-root': {
          padding: theme.spacing(0),
        },
        '.MuiDialogContent-root .simplebar-container': {
          padding: theme.spacing(0, 2),
        },
        '.MuiDialogActions-root': {
          padding: theme.spacing(2),
          justifyContent: 'flex-end',
        },
        '.MuiAvatar-circular': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          svg: {
            width: '18px',
            height: '18px',
          },
          'path, circle': {
            fill: theme.palette.primary.main,
          },
        },
        '.MuiAlert-root': {
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(2),
          borderRadius: theme.shape.borderRadius,
        },
        '.MuiInputLabel-root': {
          position: 'relative',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          legend: {
            overflow: 'visible',
            span: {
              opacity: 1,
              bgcolor: '#fff',
            },
          },
        },
        '& .MuiTypography-root': {
          fontSize: '0.8em',
        },
        '& .MuiListItemText-root': {
          padding: 0,
          margin: 0,
        },
        '& .MuiListItemButton-root': {
          padding: '4px 0',
        },
      })}>
      <DialogTitle>
        <Avatar>
          <TransportGroupRoundedIcon fontSize='small' />
        </Avatar>
        <Typography component='p'>Добавить Дивизион или автоколонну</Typography>
        <IconButton color='default' onClick={onClose}>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Scrollbar>
          <FormControl margin='normal' variant='outlined' fullWidth onSubmit={handleFormSubmit}>
            <Select value={type} label='Дивизион или автоколонна?' onChange={handleChangeType}>
              <MenuItem value={'division'}>Дивизион</MenuItem>
              <MenuItem value={'autocolon'}>Автоколонна</MenuItem>
            </Select>
            {type === 'autocolon' && (
              <>
                <Stack sx={{ paddingTop: '10px' }}>
                  <OutlinedInput
                    label='Автоколонна'
                    variant='outlined'
                    notched
                    fullWidth
                    value={name}
                    onChange={handleChangeName}
                    placeholder='Название Автоколонны'></OutlinedInput>
                  <FormHelperText>Используйте в названии не менее 3 символов</FormHelperText>
                </Stack>
                <Stack sx={{ paddingTop: '10px' }}>
                  <Select value={selectedDivision} label='Дивизион' variant='outlined' onChange={handleChangeDivision}>
                    <MenuItem value={0} key={0}>
                      Отвязать отовсех
                    </MenuItem>
                    {divisions.map((division) => (
                      <MenuItem value={division.id} key={division.id}>
                        {division.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </>
            )}
            {type === 'division' && (
              <>
                <Stack sx={{ paddingTop: '10px' }}>
                  <OutlinedInput
                    label='Дивизион'
                    variant='outlined'
                    notched
                    fullWidth
                    value={name}
                    onChange={handleChangeName}
                    placeholder='Название Дивизиона'></OutlinedInput>
                  <FormHelperText>Используйте в названии не менее 3 символов</FormHelperText>
                </Stack>
                <Stack direction={'row'} sx={{ width: '100%', paddingTop: '10px' }}>
                  <Stack sx={{ width: '50%' }}>
                    <Typography align='center' color='text.secondary' variant='caption' component='div'>
                      Привязанные автоколонны
                    </Typography>
                    <List>
                      {selectedAutocolons.map(({ id, name }) => {
                        return (
                          <ListItemButton key={id} onClick={() => handleToggleAutocolon({ id, name })}>
                            <ListItemText primary={name} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Stack>

                  <Stack
                    sx={{
                      paddingTop: '10px',
                      width: '50%',
                    }}>
                    <Typography align='center' color='text.secondary' variant='caption' component='div'>
                      Нераспределенные автоколонны
                    </Typography>
                    <List>
                      {unselectedAutocolons.map(({ id, name }) => {
                        return (
                          <ListItemButton key={id} onClick={() => handleToggleAutocolon({ id, name })}>
                            <ListItemText primary={name} />
                          </ListItemButton>
                        );
                      })}
                      {newAutocolons().map(({ id, name }) => {
                        return (
                          <ListItemButton
                            key={id}
                            onClick={() =>
                              handleToggleAutocolon({
                                id,
                                name,
                              })
                            }>
                            <ListItemText primary={name} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Stack>
                </Stack>
              </>
            )}
          </FormControl>
          {!isCreateLoading && isCreateError && (
            <Alert severity='error'>Произошла ошибка при добавлении. Попробуйте снова или обратитесь в техническую поддержку.</Alert>
          )}
        </Scrollbar>
      </DialogContent>
      <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
        <DialogActions>
          <LoadingButton variant='contained' loading={isCreateLoading} onClick={onClose} disableElevation>
            Отмена
          </LoadingButton>
        </DialogActions>
        <DialogActions>
          <LoadingButton variant='contained' loading={isCreateLoading} disableElevation onClick={handleFormSubmit}>
            Добавить
          </LoadingButton>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

export default AutocolonDialog;
